import { cloneDeep, isEmpty } from 'lodash-es';

import { ISmallSite } from '~/src/dash/api/channels/channels_models';
import {
  defaultInventoryParameters,
  EGroupByType,
  IDeliveryVariables,
  IInventoryParameters,
} from '~/src/dash/apiV2/forecasting/forecasting';
import { addCommasToIntegerOrFloat } from '~/src/dash/utils/basic';
import { ICSVColumn } from '~/src/dash/views/dashboards/DashboardHome/sections/DashboardOverview/CSVDownloadButton';
import { timePeriods } from '~/src/dash/views/tools/Forecasting/DeliveryVariables/FrequencyCapping';

export interface IForecastingCards {
  TotalPods: number;
  TotalSlots: number;
  AvgSlotsPerPod: number;
  AvgFillRate: number;
  TotalExpectedImps: number;
  TotalBookedImps: number;
  RenderablePods: number;
  RenderableAvails: number;
  AvgSlotLength: number;
}

export interface IForecastingData {
  Tier: string;
  Sites: ISmallSite[];
  SiteName: string;
  Avails: number;
  RenderableAvails: number;
  ExpectedImps: number;
  AvgCPM: number;
  ExpectedRevenue: number;
  BookedImps: number;
}

export interface IOverlappingLineItems {
  ID: number;
  Name: string;
  Tier: number;
  ImpressionGoal: number;
  DeliveryPercentage: number;
  FlightDates: string;
  NetPrice: number;
  OrderID: number;
}

export interface IForecastingResponse {
  GeneratedAt: string;
  ForecastingCards: IForecastingCards;
  ForecastingData: IForecastingData[];
  ForecastingDataSummed: IForecastingData;
  OverlappingLineItems: IOverlappingLineItems[];
}

export const generateCSVColumns = (enableBookedImps: boolean) => {
  const columns: ICSVColumn[] = [
    {
      id: 'Col1',
      displayName: '',
    },
    {
      id: 'Col2',
      displayName: '',
    },
    {
      id: 'Col3',
      displayName: '',
    },
    {
      id: 'Col4',
      displayName: '',
    },
    {
      id: 'Col5',
      displayName: '',
    },
    {
      id: 'Col6',
      displayName: '',
    },
  ];
  if (enableBookedImps) {
    columns.push(
      {
        id: 'Col7',
        displayName: '',
      },
      {
        id: 'Col8',
        displayName: '',
      },
    );
  }
  return columns;
};

const formatInventoryParameters = (value: any) => {
  if (typeof value === 'object') {
    return JSON.stringify(value);
  } else {
    return value;
  }
};

const formatDeliveryVariables = (
  deliveryVariables: IDeliveryVariables,
  key: string,
  value: any,
) => {
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  } else if (deliveryVariables.FreqCapActive && key === 'FreqCapTimePeriod') {
    const timePeriod = timePeriods.find((tp) => tp.id === value);
    return timePeriod?.label;
  } else if (key === 'TrafficAdjustment' && isNaN(deliveryVariables.TrafficAdjustment)) {
    return 0;
  } else {
    return value;
  }
};

export const formatDataToCSV = (
  columns: ICSVColumn[],
  publisherID: string,
  forecastingData: IForecastingResponse,
  inventoryParametersData: IInventoryParameters,
  deliveryVariablesData: IDeliveryVariables,
  enableBookedImps: boolean,
  currencySymbol: string,
  currencyMultiplier: number,
  groupByType: EGroupByType,
) => {
  const convertToPublisherCurrencyValue = (value: string): string => {
    return `${currencySymbol}${addCommasToIntegerOrFloat(
      (parseFloat(value) * currencyMultiplier).toFixed(2),
    )}`;
  };

  const convertToPublisherCurrencyValueRounded = (value: string): string => {
    return `${currencySymbol}${addCommasToIntegerOrFloat(
      String(Math.round(parseFloat(value) * currencyMultiplier)),
    )}`;
  };

  const ignoredKeys = [
    'ActiveParameters',
    'DaypartingUI',
    'LocalTargetingAllowedUI',
    'LocalTargetingDisallowedUI',
  ];
  // INVENTORY PARAMETERS DATA
  const formattedInventoryParametersData: Record<string, string>[] = [];
  const clonedInventoryParametersData = cloneDeep(inventoryParametersData);
  if (!isEmpty(clonedInventoryParametersData)) {
    for (const key in clonedInventoryParametersData) {
      if (
        !ignoredKeys.includes(key) &&
        !isEmpty(clonedInventoryParametersData[key as keyof IInventoryParameters]) &&
        clonedInventoryParametersData[key as keyof IInventoryParameters] !==
          defaultInventoryParameters[key as keyof IInventoryParameters]
      ) {
        // insert title of section when we know we have a modified filter
        if (isEmpty(formattedInventoryParametersData)) {
          formattedInventoryParametersData.push({
            Col1: 'INVENTORY PARAMETERS',
          });
        }

        const formattedValue = formatInventoryParameters(
          clonedInventoryParametersData[key as keyof IInventoryParameters],
        );
        formattedInventoryParametersData.push({
          Col1: key,
          Col2: String(formattedValue),
        });
      }
    }
  }

  // DELIVERY VARIABLES DATA
  const formattedDeliveryVariablesData: Record<string, string>[] = [];
  const excludedDeliveryVariablesKeys = [
    'FreqCapImps',
    'FreqCapTimePeriodCount',
    'FreqCapTimePeriod',
  ];
  const clonedDeliveryVariablesData = cloneDeep(deliveryVariablesData);
  if (!isEmpty(clonedDeliveryVariablesData)) {
    for (const key in clonedDeliveryVariablesData) {
      if (clonedDeliveryVariablesData[key as keyof IDeliveryVariables] !== undefined) {
        // insert title of section when we know we have a modified filter
        if (isEmpty(formattedDeliveryVariablesData)) {
          formattedDeliveryVariablesData.push({
            Col1: 'DELIVERY VARIABLES',
          });
        }

        const formattedValue = formatDeliveryVariables(
          clonedDeliveryVariablesData,
          key,
          clonedDeliveryVariablesData[key as keyof IDeliveryVariables],
        );
        // do not insert FreqCapImps, FreqCapTimePeriodCount, and FreqCapTimePeriod if frequency cap is inactive
        // if frequency cap is active, include all fields
        if (
          deliveryVariablesData.FreqCapActive ||
          (!deliveryVariablesData.FreqCapActive && !excludedDeliveryVariablesKeys.includes(key))
        ) {
          formattedDeliveryVariablesData.push({
            Col1: key,
            Col2: String(formattedValue),
          });
        }
      }
    }
  }

  // CARDS DATA
  const includedForecastCardKeysForFreqCap = ['TotalPods', 'RenderablePods'];
  const formattedForecastingCardsData: Record<string, string>[] = [];
  const clonedForecastingData = cloneDeep(forecastingData);

  if (!isEmpty(clonedForecastingData.ForecastingCards)) {
    for (const key in clonedForecastingData.ForecastingCards) {
      if (clonedForecastingData.ForecastingCards[key as keyof IForecastingCards] !== undefined) {
        // insert title of section when we know we have a modified filter
        if (isEmpty(formattedForecastingCardsData)) {
          formattedForecastingCardsData.push({
            Col1: 'FORECASTED DATA',
          });
        }
        // only show TotalPods and RenderablePods if freq cap is active, otherwise show every field
        if (
          !deliveryVariablesData.FreqCapActive ||
          (deliveryVariablesData.FreqCapActive && includedForecastCardKeysForFreqCap.includes(key))
        ) {
          if (
            groupByType === EGroupByType.Site &&
            (key === 'AvgFillRate' || key === 'TotalBookedImps')
          ) {
            // Site does not include data for AvgFillRate and TotalBookedImps
            continue;
          } else {
            if (key === 'AvgFillRate') {
              formattedForecastingCardsData.push({
                Col1: key,
                Col2: clonedForecastingData.ForecastingCards[
                  key as keyof IForecastingCards
                ].toFixed(2),
              });
            } else {
              formattedForecastingCardsData.push({
                Col1: key,
                Col2: addCommasToIntegerOrFloat(
                  String(clonedForecastingData.ForecastingCards[key as keyof IForecastingCards]),
                ),
              });
            }
          }
        }
      }
    }
  }

  // BREAKDOWN TABLE DATA
  const formattedBreakdownTableData: Record<string, string>[] = [];
  if (!deliveryVariablesData.FreqCapActive) {
    const clonedBreakdownTableData = cloneDeep(forecastingData.ForecastingData);
    if (!isEmpty(clonedBreakdownTableData)) {
      // insert title of section when we know we have a modified filter
      if (isEmpty(formattedBreakdownTableData)) {
        formattedBreakdownTableData.push({
          Col1: 'BREAKDOWN TABLE',
        });
      }

      if (groupByType === EGroupByType.Tier) {
        let counter = 1;
        const excludedBreakdownTableHeaders = ['SiteName'];
        const finalBreakdownTableHeaders = {} as Record<string, string>;
        for (const key in forecastingData.ForecastingData[0]) {
          if (
            forecastingData.ForecastingData[0][key as keyof IForecastingData] !== undefined &&
            !excludedBreakdownTableHeaders.includes(key)
          ) {
            finalBreakdownTableHeaders[`Col${counter}`] = key;
            counter++;
          }
        }
        formattedBreakdownTableData.push(finalBreakdownTableHeaders);
        // add in the 'All' summed row
        const clonedBreakdownTableSummedData = cloneDeep(forecastingData.ForecastingDataSummed);
        if (enableBookedImps) {
          formattedBreakdownTableData.push({
            Col1: 'All',
            Col2: addCommasToIntegerOrFloat(
              String(clonedBreakdownTableSummedData.Sites?.length || '0'),
            ),
            Col3: addCommasToIntegerOrFloat(String(clonedBreakdownTableSummedData.Avails || '0')),
            Col4: addCommasToIntegerOrFloat(
              String(clonedBreakdownTableSummedData.RenderableAvails || '0'),
            ),
            Col5: addCommasToIntegerOrFloat(
              String(clonedBreakdownTableSummedData.ExpectedImps || '0'),
            ),
            Col6: convertToPublisherCurrencyValue(
              String(clonedBreakdownTableSummedData.AvgCPM) || '0',
            ),
            Col7: convertToPublisherCurrencyValueRounded(
              String(clonedBreakdownTableSummedData.ExpectedRevenue) || '0',
            ),
            Col8: addCommasToIntegerOrFloat(
              String(clonedBreakdownTableSummedData.BookedImps || '0'),
            ),
          });
        } else {
          formattedBreakdownTableData.push({
            Col1: 'All',
            Col2: addCommasToIntegerOrFloat(
              String(clonedBreakdownTableSummedData.Sites?.length || '0'),
            ),
            Col3: addCommasToIntegerOrFloat(String(clonedBreakdownTableSummedData.Avails || '0')),
            Col4: addCommasToIntegerOrFloat(
              String(clonedBreakdownTableSummedData.RenderableAvails || '0'),
            ),
            Col5: addCommasToIntegerOrFloat(
              String(clonedBreakdownTableSummedData.ExpectedImps || '0'),
            ),
            Col6: convertToPublisherCurrencyValue(
              String(clonedBreakdownTableSummedData.AvgCPM) || '0',
            ),
            Col7: convertToPublisherCurrencyValueRounded(
              String(clonedBreakdownTableSummedData.ExpectedRevenue) || '0',
            ),
          });
        }
        // add in the rest of the rows of data
        clonedBreakdownTableData.forEach((row) => {
          if (enableBookedImps) {
            formattedBreakdownTableData.push({
              Col1: row.Tier,
              Col2: addCommasToIntegerOrFloat(String(row.Sites?.length || '0')),
              Col3: addCommasToIntegerOrFloat(String(row.Avails || '0')),
              Col4: addCommasToIntegerOrFloat(String(row.RenderableAvails || '0')),
              Col5: addCommasToIntegerOrFloat(String(row.ExpectedImps || '0')),
              Col6: convertToPublisherCurrencyValue(String(row.AvgCPM) || '0'),
              Col7: convertToPublisherCurrencyValueRounded(String(row.ExpectedRevenue) || '0'),
              Col8: addCommasToIntegerOrFloat(String(row.BookedImps || '0')),
            });
          } else {
            formattedBreakdownTableData.push({
              Col1: row.Tier,
              Col2: addCommasToIntegerOrFloat(String(row.Sites?.length || '0')),
              Col3: addCommasToIntegerOrFloat(String(row.Avails || '0')),
              Col4: addCommasToIntegerOrFloat(String(row.RenderableAvails || '0')),
              Col5: addCommasToIntegerOrFloat(String(row.ExpectedImps || '0')),
              Col6: convertToPublisherCurrencyValue(String(row.AvgCPM) || '0'),
              Col7: convertToPublisherCurrencyValueRounded(String(row.ExpectedRevenue) || '0'),
            });
          }
        });
      } else if (groupByType === EGroupByType.Site) {
        let counter = 1;
        const excludedBreakdownTableHeaders = ['Sites', 'BookedImps', 'Tier'];
        const finalBreakdownTableHeaders = {} as Record<string, string>;
        for (const key in forecastingData.ForecastingData[0]) {
          if (
            forecastingData.ForecastingData[0][key as keyof IForecastingData] !== undefined &&
            !excludedBreakdownTableHeaders.includes(key)
          ) {
            finalBreakdownTableHeaders[`Col${counter}`] = key;
            counter++;
          }
        }
        formattedBreakdownTableData.push(finalBreakdownTableHeaders);
        clonedBreakdownTableData.forEach((row) => {
          formattedBreakdownTableData.push({
            Col1: row.SiteName,
            Col2: addCommasToIntegerOrFloat(String(row.Avails || '0')),
            Col3: addCommasToIntegerOrFloat(String(row.RenderableAvails || '0')),
            Col4: addCommasToIntegerOrFloat(String(row.ExpectedImps || '0')),
            Col5: convertToPublisherCurrencyValue(String(row.AvgCPM) || '0'),
            Col6: convertToPublisherCurrencyValueRounded(String(row.ExpectedRevenue) || '0'),
          });
        });
      }
    }
  }

  // LINE ITEMS TABLE DATA
  const formattedLineItemsTableData: Record<string, string>[] = [];
  if (groupByType === EGroupByType.Tier) {
    const clonedLineItemsTableData = cloneDeep(forecastingData.OverlappingLineItems);
    if (!isEmpty(clonedLineItemsTableData)) {
      // insert title of section when we know we have a modified filter
      if (isEmpty(formattedLineItemsTableData)) {
        formattedLineItemsTableData.push({
          Col1: 'LINE ITEMS TABLE',
        });
      }

      let counter = 1;
      const excludedLineItemsTableHeaders = ['ID', 'OrderID'];
      const finalLineItemsTableHeaders = {} as Record<string, string>;
      for (const key in forecastingData.OverlappingLineItems[0]) {
        if (
          forecastingData.OverlappingLineItems[0][key as keyof IOverlappingLineItems] !==
            undefined &&
          !excludedLineItemsTableHeaders.includes(key)
        ) {
          finalLineItemsTableHeaders[`Col${counter}`] = key;
          counter++;
        }
      }
      formattedLineItemsTableData.push(finalLineItemsTableHeaders);
      clonedLineItemsTableData.forEach((row) => {
        formattedLineItemsTableData.push({
          Col1: row.Name,
          Col2: String(row.Tier),
          Col3: addCommasToIntegerOrFloat(String(row.ImpressionGoal || '0')),
          Col4: `${String(row.DeliveryPercentage)}%`,
          Col5: row.FlightDates,
          Col6: convertToPublisherCurrencyValue(String(row.NetPrice)),
        });
      });
    }
  }

  // PUT DATA TOGETHER
  const formattedData: Record<string, string>[] = [
    {
      Col1: 'Publisher ID',
      Col2: publisherID,
    },
    {},
    ...(!isEmpty(formattedInventoryParametersData)
      ? [...formattedInventoryParametersData, {}]
      : []),
    ...(!isEmpty(formattedDeliveryVariablesData) ? [...formattedDeliveryVariablesData, {}] : []),
    ...(!isEmpty(formattedForecastingCardsData) ? [...formattedForecastingCardsData, {}] : []),
    ...(!isEmpty(formattedBreakdownTableData) ? [...formattedBreakdownTableData, {}] : []),
    ...(!isEmpty(formattedLineItemsTableData) ? [...formattedLineItemsTableData, {}] : []),
  ];

  // Clean the data so all rows match the column keys
  const cleanData = formattedData.map((row) => {
    const cleanRow: Record<string, string> = {};
    columns.forEach((col) => {
      cleanRow[col.id] = row[col.id] ?? ''; // Replace undefined with ''
    });
    return cleanRow;
  });

  return Promise.resolve(cleanData);
};
